import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Contact, ContactType } from '../types';
import { Card, CardContent, Button, Badge, EmptyState, Dialog} from '../components/ui';
import Layout from '../components/Layout';

const CONTACT_TYPES: { value: ContactType; label: string }[] = [
  { value: 'owner', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'seller', label: 'Seller' },
  { value: 'broker', label: 'Broker' },
  { value: 'lender', label: 'Lender' },
  { value: 'attorney', label: 'Attorney' },
  { value: 'property_manager', label: 'Property Manager' },
  { value: 'investor', label: 'Investor' },
  { value: 'developer', label: 'Developer' },
  { value: 'appraiser', label: 'Appraiser' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' },
];

const typeBadgeVariant = (type: ContactType) => {
  const map: Record<string, 'info' | 'success' | 'warning' | 'error' | 'primary' | 'default'> = {
    owner: 'primary', tenant: 'info', buyer: 'success', seller: 'warning',
    broker: 'primary', lender: 'info', investor: 'success', developer: 'warning',
  };
  return map[type] || 'default';
};

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('last_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const PAGE_SIZE = 50;
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', mobile_phone: '',
    title: '', contact_type: 'other' as ContactType, company_id: '', notes: '',
  });

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (search) params.search = search;
      if (typeFilter) params.contact_type = typeFilter;
      const res = await api.listContacts(params);
      setContacts(res.contacts || []);
      setTotal(res.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, page, sortBy, sortDir]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Reset to the first page whenever the search, filter, or sort changes.
  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, sortBy, sortDir]);

  // Click a column header: toggle direction if already sorted by it, else sort asc.
  const toggleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };
  const sortArrow = (col: string) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchContacts();
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.company_id) delete (payload as any).company_id;
      await api.createContact(payload);
      setShowModal(false);
      setForm({ first_name: '', last_name: '', email: '', phone: '', mobile_phone: '', title: '', contact_type: 'other', company_id: '', notes: '' });
      fetchContacts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div>
        <div className="pb-4">
          {/* Wraps below ~250px, and the title block may shrink - the OO4/OO5 header pattern.
              Without it the action button was pushed past the viewport on the narrow pane. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="min-w-0">
              <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Contacts</h1>
              <p className="text-gray-500 mt-1">{total} contacts in your CRM</p>
            </div>
            <Button onClick={() => setShowModal(true)}>+ Add Contact</Button>
          </div>
        </div>
      </div>

      <div className="pt-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          {/* ⭐ `basis` sets the WRAP POINT; `min-w-0` lets it shrink BELOW that when the viewport
              itself is narrower. `min-w-[280px]` did the first job and refused the second, so at
              250px - the narrowest Joseph's pane runs - this form was 280px wide and pushed the
              whole PAGE 46px sideways. Measured: the form's right edge was 296 in a 250 viewport.
              Same fix as the OO4 header row in DataHub. */}
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 basis-[280px] min-w-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Button type="submit" variant="outline" size="sm">Search</Button>
          </form>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">All Types</option>
            {CONTACT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>

          {/* Sort by */}
          <div className="flex items-center gap-1">
            <label className="text-sm text-gray-500">Sort by</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="last_name">Name</option>
              <option value="contact_type">Type</option>
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="title">Title</option>
              <option value="created_at">Date Added</option>
            </select>
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
              aria-label="Toggle sort direction"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              {sortDir === 'asc' ? '↑ A–Z' : '↓ Z–A'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">{error}</div>
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
                <p className="text-gray-500 mt-2">Loading contacts...</p>
              </div>
            ) : contacts.length === 0 ? (
              <EmptyState
                title="No contacts found"
                description="Add your first contact to start building your CRM."
                action={{ label: 'Add Contact', onClick: () => setShowModal(true) }}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th
                        className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer select-none hover:text-primary-600"
                        onClick={() => toggleSort('last_name')}
                      >
                        Name{sortArrow('last_name')}
                      </th>
                      <th
                        className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer select-none hover:text-primary-600"
                        onClick={() => toggleSort('contact_type')}
                      >
                        Type{sortArrow('contact_type')}
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Company</th>
                      <th
                        className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer select-none hover:text-primary-600"
                        onClick={() => toggleSort('email')}
                      >
                        Email{sortArrow('email')}
                      </th>
                      <th
                        className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer select-none hover:text-primary-600"
                        onClick={() => toggleSort('phone')}
                      >
                        Phone{sortArrow('phone')}
                      </th>
                      <th
                        className="px-4 py-3 text-left font-medium text-gray-700 cursor-pointer select-none hover:text-primary-600"
                        onClick={() => toggleSort('title')}
                      >
                        Title{sortArrow('title')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contacts.map((c) => (
                      <tr
                        key={c.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {c.first_name} {c.last_name}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={typeBadgeVariant(c.contact_type)}>
                            {c.contact_type?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {c.company?.name || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{c.email || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{c.phone || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{c.title || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination. The row WRAPS: at 250px the count line and the Prev/Next controls do not
            fit side by side, and without a wrap they pushed the page sideways. */}
        {!loading && total > PAGE_SIZE && (
          <div className="flex flex-wrap items-center justify-between gap-2 mt-4 text-sm">
            <span className="text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Prev
              </button>
              <span className="px-2 text-gray-600">Page {page} of {totalPages.toLocaleString()}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      {/* OO5d: through the Dialog primitive. */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Add Contact"
        size="lg"
        onSubmit={handleCreate}
        footer={<>
          <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Contact'}</Button>
        </>}
      >
        <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    required
                    value={form.first_name}
                    onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    required
                    value={form.last_name}
                    onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                <select
                  value={form.contact_type}
                  onChange={(e) => setForm(f => ({ ...f, contact_type: e.target.value as ContactType }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {CONTACT_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    value={form.phone}
                    onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                  <input
                    value={form.mobile_phone}
                    onChange={(e) => setForm(f => ({ ...f, mobile_phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
        </div>
      </Dialog>
    </Layout>
  );
}
